// @owners { team: patients }
import { type InsurancesEndpointCreateParams } from '@alto/scriptdash/alto/billing/patients/v3/insurances_endpoint';
import { post } from '~shared/helpers/apiHelper';
import { getErrorMessageFromResponse } from '~shared/helpers/helper';
import { queries } from '~shared/queries/query-keys';
import { invalidateDeliveries } from '~shared/queries/query-keys/deliveries';
import { useMutation, useQueryClient } from '~shared/react-query';

/**
 * Create a manual (copay) coupon insurance record — React Query replacement for the deprecated
 * createManualCoupon thunk. Resolves to the created insurance id; rejects on error.
 */
export const useCreateManualCoupon = () =>
  useMutation({
    mutationFn: async (params: InsurancesEndpointCreateParams): Promise<number> => {
      const response = await post('/billing/v1/insurances', { params });
      if (response.error) {
        throw new Error(getErrorMessageFromResponse(response));
      }
      return response.data.id;
    },
  });

type ApplyManualCouponParams = { deliveryID: number; insuranceID: number };

/**
 * Apply a manual coupon to a delivery — React Query replacement for the deprecated applyManualCoupon
 * thunk. On success refreshes order + prescription pricing and deliveries; rejects on error.
 */
export const useApplyManualCoupon = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ deliveryID, insuranceID }: ApplyManualCouponParams) => {
      const response = await post('/billing/v1/insurances/apply_coupon', {
        delivery_id: deliveryID,
        insurance_id: insuranceID,
      });
      if (response.error) {
        throw new Error(getErrorMessageFromResponse(response));
      }
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queries.pricing.order._def });
      queryClient.invalidateQueries({ queryKey: queries.pricing.prescriptions.queryKey });
      void invalidateDeliveries();
    },
  });
};

type RaiseSupportRequestParams = { deliveryID: number; insuranceID: number | null };

/**
 * Raise a manual-coupon support request (/notify_ops) — React Query replacement for the deprecated
 * raiseSupportRequest thunk. On success refreshes deliveries + pricing; rejects on error.
 */
export const useRaiseSupportRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ deliveryID, insuranceID }: RaiseSupportRequestParams) => {
      const response = await post('/billing/v1/insurances/notify_ops', {
        delivery_id: deliveryID,
        insurance_id: insuranceID,
      });
      if (response.error) {
        throw new Error(getErrorMessageFromResponse(response));
      }
      return response;
    },
    onSuccess: async () => {
      // Refresh the state that may have been modified by /notify_ops.
      await invalidateDeliveries();
      await queryClient.invalidateQueries({ queryKey: queries.pricing.prescriptions.queryKey });
      await queryClient.invalidateQueries({ queryKey: queries.pricing.order._def });
    },
  });
};
